import { hasNoElements } from "./Array";
import { getType, ObjectType } from "./Object";

export function isNullOrWhiteSpace(value: unknown): value is null | undefined {
  if (value == null || typeof value !== `string`) {
    return true;
  }

  if (value.trim().length <= 0) {
    return true;
  }

  return false;
}

export function isNotNullOrWhiteSpace(value: unknown): value is string {
  return !isNullOrWhiteSpace(value);
}

export function isNullOrEmpty(value: string): boolean {
  if (value == null || typeof value !== `string`) {
    return true;
  }

  if (value.length <= 0) {
    return true;
  }

  return false;
}

export function isNotNullOrEmpty(value: string): boolean {
  return !isNullOrEmpty(value);
}

/**
 * @param [separator=" "] Default is 1 space.
 */
export function joinNonEmpty(values: string[], separator = ` `): string {
  if (hasNoElements(values)) {
    return ``;
  }

  return values.reduce((out, value) => {
    if (typeof value !== `string`) {
      return out;
    }

    if (isNullOrEmpty(value)) {
      return out;
    }

    let newOut = out;
    if (isNotNullOrEmpty(out)) {
      newOut = out + separator;
    }

    return newOut + value;
  }, ``);
}

export function isString(value: unknown): value is string {
  return getType(value) === ObjectType.String;
}

export function asNonWhiteSpaceString(value: unknown): string {
  if (!isString(value)) {
    return null;
  }

  return isNotNullOrWhiteSpace(value) ? value : null;
}

export function replaceTemplateVariables(template: string, variables?: Record<string, string>): string {
  if (variables == null) {
    return template;
  }
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const fn = new Function(...Object.keys(variables), `return \`${template}\`;`);
  return fn(...Object.values(variables));
}

let measuringContext = null;
function getVisualTextWidth(text: string, font: string): number {
  if (measuringContext == null) {
    measuringContext = document?.createElement(`canvas`).getContext(`2d`);
  }
  if (measuringContext.font !== font && font != null) {
    measuringContext.font = font;
  }
  const { width } = measuringContext.measureText(text);
  return width;
}

export function fitTextListToContent(origin: string[], maxWidth: number, font: string, separator = `, `): string {
  if (!Array.isArray(origin)) {
    return ``;
  }

  if (origin.length === 1) {
    return origin[0];
  }

  let copy = origin.map((o) => o?.toString()?.trim() ?? ``);
  let text = copy.join(separator);
  while (getVisualTextWidth(text, font) > maxWidth) {
    copy = copy.slice(0, copy.length - 1);
    const trimmedText = copy.join(separator);
    text = `${trimmedText} (+${origin.length - copy.length})`;

    if (copy.length === 1 && getVisualTextWidth(text, font) > maxWidth) {
      text = fitTextToContent(copy[0], maxWidth, font, `... (+${origin.length - 1})`);
    }
  }

  return text;
}

function fitTextToContent(origin: string, maxWidth: number, font: string, suffix = `...`): string {
  let text = `${origin.trim()}${suffix}`;
  let trimLength = 0;
  while (getVisualTextWidth(text, font) > maxWidth) {
    const trimmedText = origin.substring(0, origin.length - ++trimLength).trim();
    text = `${trimmedText}${suffix}`;
  }

  return text;
}

export function truncateUrl(url: string): string {
  const truncatedUrl = url.replace(/^(https?:\/\/)?/i, ``).replace(/\?.*?$/i, ``);

  const urlSegments = truncatedUrl.split(`/`);
  return [
    [urlSegments.slice(0, 1), `/`].join(``),
    ...[`/${urlSegments.slice(urlSegments.length - 2, urlSegments.length).join(`/`)}`]
  ].join(`...`);
}

export function sanitizeTextInput(text: string): string {
  const node = document.createElement(`div`);
  node.innerHTML = text;
  const content = node.textContent ?? node.innerText ?? text;
  return content?.trim();
}

export function endsWithWhitespace(text: string): boolean {
  // test: https://regex101.com/r/skx8F6/1
  return /\s+$/i.test(text);
}

export function endsWithoutWhitespace(text: string): boolean {
  return !endsWithWhitespace(text);
}

export function escapeAllQuoteTypes(text: string): string {
  return text.replace(/"/g, `\\"`).replace(/'/g, `\\'`);
}

export function unescapeAllQuoteTypes(text: string): string {
  return text.replace(/\\"/g, `"`).replace(/\\'/g, `'`);
}
